import {Editor, Transforms} from "slate";

import {isListItemNode, unwrapList} from "../helpers/list-helpers.ts";

/**
 * This is a Slate plugin that adds support for resetting the block type to
 * the default (paragraph) when a line-break is inserted. Specifically, it
 * does two things:
 * - Changes the default behavior of insertBreak to always reset the block
 *   type after starting a new block. This means that, for example, pressing
 *   enter after typing a heading will not give you another heading node after,
 *   but a paragraph. Within a list, however, pressing enter will add another
 *   list-item below.
 * - Adds an insertHardBreak method on the editor instance which is a stricter
 *   form of insertBreak. The difference is that, when a hard-break is used,
 *   list nodes are unwrapped, leaving you with a plain top-level paragraph
 *   rather than adding a new list-item.
 */
export const withHardBreak = (editor: Editor): Editor => {
    const {insertBreak} = editor;

    editor.insertBreak = () => {
        // Run normal insertBreak logic (which splits the node at selection):
        insertBreak();

        // Reset the block type to paragraph:
        Transforms.setNodes(editor, {type: "paragraph"});

        // Clear marks (inline formatting, like bold):
        editor.marks = {};
    };

    editor.insertHardBreak = () => {
        // Run normal insertBreak logic (which splits the node at selection):
        insertBreak();

        Editor.withoutNormalizing(editor, () => {
            const parentListItem = Editor.above(editor, {
                match: isListItemNode,
            });
            if (parentListItem) {
                // If we're inside a list, we need to take care to unwrap all
                // the nested blocks that comprise a list.
                unwrapList(editor);
            } else {
                // Otherwise, we just switch the block type to paragraph.
                Transforms.setNodes(editor, {type: "paragraph"});
            }

            // Clear marks (inline formatting, like bold):
            editor.marks = {};
        });
    };

    return editor;
};
